import { jsxs, jsx } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { MenuRenderer, ButtonIcon, IconShare, Box, Bleed, MenuItem, IconMail, IconSocialFacebook, IconSocialTwitter, IconSocialLinkedIn, Inline, Text } from "braid-design-system";
import { useMemo, useCallback } from "react";
import { useAnalyticsHandlers } from "../JobDetailsView/JobDetailsView.mjs";
import { useJobUrl } from "../JDVLayout/JDVLayout.mjs";
import { translations } from "../../../components/.vocab/index.mjs";
import { textHoverUnderline } from "./ShareMenu.css.mjs";
function encodeUrl(baseUrl, query) {
  const encodedQuery = Object.entries(query).reduce(
    (memo, [key, value], index) => `${memo}${index > 0 ? "&" : ""}${encodeURIComponent(
      key
    )}=${encodeURIComponent(value)}`,
    ""
  );
  return encodeURIComponent(
    `${baseUrl}${baseUrl.indexOf("?") === -1 ? "?" : "&"}${encodedQuery}`
  );
}
const useShareHandler = (options) => {
  const { t } = useTranslations(translations);
  const [href, width, height] = useMemo(() => {
    switch (options.channel) {
      case "facebook": {
        const url = encodeUrl(options.url, { share: "fb" });
        return [`https://www.facebook.com/sharer.php?u=${url}`, 620, 436];
      }
      case "linkedin": {
        const url = encodeUrl(options.url, { share: "li" });
        return [
          `https://www.linkedin.com/shareArticle?mini=true&url=${url}`,
          620,
          338
        ];
      }
      case "twitter": {
        const url = encodeUrl(options.url, { share: "twt" });
        const text = encodeURIComponent(t("Check out this job on SEEK:"));
        return [`https://twitter.com/share?text=${text}&url=${url}`, 620, 338];
      }
      default: {
        return [];
      }
    }
  }, [options, t]);
  const emailShare = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }
      window.location.href = options.url;
      if (options.track) {
        options.track();
      }
    },
    [options]
  );
  const socialShare = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }
      window.open(
        href,
        "sharer",
        `toolbar=0,status=0,width=${width},height=${height}`
      );
      if (options.track) {
        options.track();
      }
    },
    [href, height, width, options]
  );
  return options.channel === "email" ? emailShare : socialShare;
};
const getEmailShareMailtoUrl = ({
  jobTitle,
  jobShareUrl,
  emailSubjectText,
  emailBodyOpeningText,
  emailBodySectionText
}) => {
  const emailSubject = encodeURIComponent(`${emailSubjectText} - ${jobTitle}`);
  const emailBody = encodeURIComponent(
    `${emailBodyOpeningText}

${emailBodySectionText}

${jobTitle} -
${jobShareUrl}`
  );
  return `mailto:%20?subject=${emailSubject}&body=${emailBody}`;
};
const ShareMenu = ({
  job,
  bleed,
  showText,
  onAnalyticsEvent = () => {
  }
}) => {
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const { t } = useTranslations(translations);
  const jobUrl = useJobUrl({
    id: job.id
  });
  const url = job.shareLink || job.url || jobUrl;
  const onFbClick = useShareHandler({
    channel: "facebook",
    url,
    track: analytics.onShareFacebookClick
  });
  const onTwitterClick = useShareHandler({
    channel: "twitter",
    url,
    track: analytics.onShareTwitterClick
  });
  const onLinkedInClick = useShareHandler({
    channel: "linkedin",
    url,
    track: analytics.onShareLinkedInClick
  });
  const emailShareUrl = useMemo(
    () => getEmailShareMailtoUrl({
      jobTitle: t("{jobTitle}", { jobTitle: job.title }),
      jobShareUrl: url,
      emailSubjectText: t("I found you a job on SEEK!"),
      emailBodyOpeningText: t("Hey,"),
      emailBodySectionText: t(
        "I found this job via SEEK and thought of you."
      )
    }),
    [job, url, t]
  );
  const onEmailClick = useShareHandler({
    channel: "email",
    url: emailShareUrl,
    track: analytics.onShareEmailClick
  });
  const menuCta = /* @__PURE__ */ jsxs(
    MenuRenderer,
    {
      align: "right",
      width: "small",
      offsetSpace: "xsmall",
      trigger: (triggerProps) => {
        const theButtonIcon = /* @__PURE__ */ jsx(
          ButtonIcon,
          {
            id: "shareMenuButton",
            icon: /* @__PURE__ */ jsx(IconShare, {}),
            label: t("Share"),
            bleed,
            data: { automation: "job-details-menu" },
            ...triggerProps
          }
        );
        return bleed ? /* @__PURE__ */ jsx(Box, { paddingX: "xsmall", children: /* @__PURE__ */ jsx(Bleed, { vertical: "xxsmall", children: theButtonIcon }) }) : /* @__PURE__ */ jsx(Box, { overflow: "hidden", children: theButtonIcon });
      },
      children: [
        /* @__PURE__ */ jsx(
          MenuItem,
          {
            icon: /* @__PURE__ */ jsx(IconMail, {}),
            onClick: onEmailClick,
            data: { automation: "send-job-link" },
            children: t("Email")
          }
        ),
        /* @__PURE__ */ jsx(MenuItem, { icon: /* @__PURE__ */ jsx(IconSocialFacebook, {}), onClick: onFbClick, children: t("Facebook") }),
        /* @__PURE__ */ jsx(MenuItem, { icon: /* @__PURE__ */ jsx(IconSocialTwitter, {}), onClick: onTwitterClick, children: t("Twitter") }),
        /* @__PURE__ */ jsx(MenuItem, { icon: /* @__PURE__ */ jsx(IconSocialLinkedIn, {}), onClick: onLinkedInClick, children: t("LinkedIn") })
      ]
    }
  );
  if (showText) {
    return /* @__PURE__ */ jsx(
      Box,
      {
        component: "label",
        cursor: "pointer",
        className: textHoverUnderline,
        children: /* @__PURE__ */ jsxs(Inline, { space: "xsmall", alignY: "center", children: [
          /* @__PURE__ */ jsx(Text, { tone: "secondary", size: "small", children: t("Share") }),
          menuCta
        ] })
      }
    );
  }
  return menuCta;
};
export {
  ShareMenu
};
